import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { 
    MessageSquare, 
    Clock, 
    CheckCircle2, 
    XCircle, 
    Play,
    Plus,
    Trash2,
    Filter,
    FileText,
    Settings,
    AlignLeft,
    X,
    Lock,
    PhoneOff
} from "lucide-react";
import { Message, MessageCondition, MessageType } from "@/api/tools/tools.types";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SwitchWithLabel } from "@/components/ui/switch-with-label";
import { NumberInput } from "@/components/ui/number-input";

interface MessagesFormProps {
    value: Message[];
    onChange: (data: Message[]) => void;
    isReadOnly?: boolean;
}

const MESSAGE_TYPES = [
    { value: "request-start", label: "Request Start", icon: Play },
    { value: "request-complete", label: "Request Complete", icon: CheckCircle2 },
    { value: "request-failed", label: "Request Failed", icon: XCircle },
    { value: "request-response-delayed", label: "Request Delayed", icon: Clock }
] as const;

const MESSAGE_TYPE_OPTIONS = MESSAGE_TYPES.map(t => ({ value: t.value, label: t.label }));

const OPERATOR_OPTIONS = [
    { value: "eq", label: "Equals (=)" },
    { value: "neq", label: "Not Equals (≠)" },
    { value: "gt", label: "Greater Than (>)" },
    { value: "gte", label: "Greater Than or Equal (≥)" },
    { value: "lt", label: "Less Than (<)" },
    { value: "lte", label: "Less Than or Equal (≤)" }
];

export function MessagesForm({ value, onChange, isReadOnly = false }: MessagesFormProps) {
    const [messages, setMessages] = useState<Message[]>(value || []);

    // Sync with parent on messages change
    useEffect(() => {
        const isEqual = JSON.stringify(messages) === JSON.stringify(value);
        if (!isEqual && messages.length >= 0) {
            onChange(messages);
        }
    }, [messages]);

    // Sync local state when value prop changes from parent
    useEffect(() => {
        const isEqual = JSON.stringify(messages) === JSON.stringify(value);
        if (!isEqual) {
            setMessages(value || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const addMessage = useCallback((type: MessageType) => {
        const newMessage: Message = {
            type,
            content: "",
            conditions: []
        };
        
        // Add type-specific defaults
        if (type === "request-start") {
            (newMessage as any).blocking = false;
        } else if (type === "request-response-delayed") {
            (newMessage as any).timingMilliseconds = 1000;
        }
        
        setMessages([...messages, newMessage]);
    }, [messages]);
    
    // Get available message types (exclude already added ones)
    const getAvailableMessageTypes = useCallback(() => {
        const existingTypes = new Set(messages.map(m => m.type));
        return MESSAGE_TYPES.filter(type => !existingTypes.has(type.value));
    }, [messages]);

    const removeMessage = useCallback((index: number) => {
        setMessages(messages.filter((_, i) => i !== index));
    }, [messages]);

    const updateMessage = useCallback((index: number, field: string, value: any) => {
        setMessages(messages.map((msg, i) => {
            if (i === index) {
                return { ...msg, [field]: value };
            }
            return msg;
        }));
    }, [messages]);

    const addCondition = useCallback((messageIndex: number) => {
        const newCondition: MessageCondition = {
            param: "",
            value: "",
            operator: "eq"
        };
        setMessages(messages.map((msg, i) => {
            if (i === messageIndex) {
                return {
                    ...msg,
                    conditions: [...(msg.conditions || []), newCondition]
                };
            }
            return msg;
        }));
    }, [messages]);

    const updateCondition = useCallback((messageIndex: number, conditionIndex: number, field: keyof MessageCondition, value: any) => {
        setMessages(messages.map((msg, i) => {
            if (i === messageIndex) {
                const updatedConditions = (msg.conditions || []).map((cond, j) => {
                    if (j === conditionIndex) {
                        return { ...cond, [field]: value };
                    }
                    return cond;
                });
                return { ...msg, conditions: updatedConditions };
            }
            return msg;
        }));
    }, [messages]);

    const removeCondition = useCallback((messageIndex: number, conditionIndex: number) => {
        setMessages(messages.map((msg, i) => {
            if (i === messageIndex) {
                return {
                    ...msg,
                    conditions: (msg.conditions || []).filter((_, j) => j !== conditionIndex)
                };
            }
            return msg;
        }));
    }, [messages]);

    const getMessageTypeIcon = (type: string) => {
        const messageType = MESSAGE_TYPES.find(t => t.value === type);
        return messageType ? messageType.icon : MessageSquare;
    };

    const getMessageTypeLabel = (type: string) => {
        const messageType = MESSAGE_TYPES.find(t => t.value === type);
        return messageType ? messageType.label : type;
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
        >
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <MessageSquare className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Response Messages</CardTitle>
                            <CardDescription>
                                Configure messages for different request stages
                            </CardDescription>
                        </div>
                        {messages.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        size="sm"
                                        disabled={isReadOnly || getAvailableMessageTypes().length === 0}
                                        className="gap-2"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Message
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    {getAvailableMessageTypes().map((type) => {
                                        const Icon = type.icon;
                                        return (
                                            <DropdownMenuItem
                                                key={type.value}
                                                onClick={() => addMessage(type.value)}
                                            >
                                                <Icon className="h-4 w-4 mr-2" />
                                                {type.label}
                                            </DropdownMenuItem>
                                        );
                                    })}
                                    {getAvailableMessageTypes().length === 0 && (
                                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                            All message types added
                                        </div>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <MessageSquare className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-sm font-medium mb-1">No Messages Configured</h3>
                            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                                Add response messages for different stages of tool execution
                            </p>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        size="sm"
                                        disabled={isReadOnly}
                                        className="gap-2"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Your First Message
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="center" className="w-56">
                                    {MESSAGE_TYPES.map((type) => {
                                        const Icon = type.icon;
                                        return (
                                            <DropdownMenuItem
                                                key={type.value}
                                                onClick={() => addMessage(type.value)}
                                            >
                                                <Icon className="h-4 w-4 mr-2" />
                                                {type.label}
                                            </DropdownMenuItem>
                                        );
                                    })}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {messages.map((message, index) => {
                                const Icon = getMessageTypeIcon(message.type);
                                return (
                                    <Card key={index} className="shadow-sm">
                                        <CardHeader className="border-b bg-muted/20 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                    <Icon className="h-3.5 w-3.5 text-primary" />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="text-sm font-medium leading-none">{getMessageTypeLabel(message.type)}</h4>
                                                    {message.content && (
                                                        <p className="text-xs text-muted-foreground/70 mt-0.5 truncate max-w-[400px]">
                                                            {message.content}
                                                        </p>
                                                    )}
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeMessage(index)}
                                                    className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2"
                                                    disabled={isReadOnly}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {/* Message Type */}
                                            <div className="space-y-2">
                                                <Label htmlFor={`type-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                    <Settings className="h-4 w-4 text-primary" />
                                                    Message Type
                                                </Label>
                                                <Combobox
                                                    options={MESSAGE_TYPE_OPTIONS}
                                                    value={message.type}
                                                    onValueChange={(value) => updateMessage(index, 'type', value as MessageType)}
                                                    placeholder="Select type..."
                                                    disabled={isReadOnly}
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className="space-y-2">
                                                <Label htmlFor={`content-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                    <AlignLeft className="h-4 w-4 text-primary" />
                                                    Message Content
                                                </Label>
                                                <Textarea
                                                    id={`content-${index}`}
                                                    value={message.content || ''}
                                                    onChange={(e) => updateMessage(index, 'content', e.target.value)}
                                                    placeholder="Enter the message content..."
                                                    className="resize-none min-h-[70px]"
                                                    disabled={isReadOnly}
                                                />
                                            </div>

                                            {/* Blocking toggle for request-start */}
                                            {message.type === "request-start" && (
                                                <SwitchWithLabel
                                                    id={`blocking-${index}`}
                                                    label="Blocking"
                                                    description="Wait for message to be spoken before triggering tool call"
                                                    icon={Lock}
                                                    checked={message.blocking || false}
                                                    onCheckedChange={(checked) => updateMessage(index, 'blocking', checked)}
                                                    disabled={isReadOnly}
                                                />
                                            )}

                                            {/* End Call After Spoken toggle for request-complete and request-failed */}
                                            {(message.type === "request-complete" || message.type === "request-failed") && (
                                                <SwitchWithLabel
                                                    id={`endCall-${index}`}
                                                    label="End Call After Spoken"
                                                    description="End the call after message is spoken"
                                                    icon={PhoneOff}
                                                    checked={('endCallAfterSpokenEnabled' in message && message.endCallAfterSpokenEnabled) || false}
                                                    onCheckedChange={(checked) => updateMessage(index, 'endCallAfterSpokenEnabled', checked)}
                                                    disabled={isReadOnly}
                                                />
                                            )}

                                            {/* Timing Milliseconds for request-response-delayed */}
                                            {message.type === "request-response-delayed" && (
                                                <div className="space-y-2">
                                                    <Label htmlFor={`timing-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                        <Clock className="h-4 w-4 text-primary" />
                                                        Delay Timing (milliseconds)
                                                    </Label>
                                                    <NumberInput
                                                        value={('timingMilliseconds' in message && message.timingMilliseconds) || 0}
                                                        onValueChange={(value) => updateMessage(index, 'timingMilliseconds', value || 0)}
                                                        min={0}
                                                        stepper={100}
                                                        placeholder="1000"
                                                        disabled={isReadOnly}
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        How long to wait before showing this delayed message
                                                    </p>
                                                </div>
                                            )}

                                            {/* Conditions Card */}
                                            <Card className="shadow-sm">
                                                <CardHeader className="border-b bg-muted/20 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                            <Filter className="h-3.5 w-3.5 text-primary" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="text-sm font-medium leading-none">Conditions (Optional)</h4>
                                                        </div>
                                                        {message.conditions && message.conditions.length > 0 && (
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                onClick={() => addCondition(index)}
                                                                disabled={isReadOnly}
                                                                className="gap-2"
                                                            >
                                                                <Plus className="h-4 w-4" />
                                                                Add Condition
                                                            </Button>
                                                        )}
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    {message.conditions && message.conditions.length > 0 ? (
                                                        message.conditions.map((condition, condIndex) => (
                                                            <Card key={condIndex} className="shadow-sm">
                                                                <CardHeader className="border-b bg-muted/20 py-2.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                                            <Filter className="h-3.5 w-3.5 text-primary" />
                                                                        </div>
                                                                        <div className="flex-1">
                                                                            <h4 className="text-sm font-medium leading-none">Condition {condIndex + 1}</h4>
                                                                        </div>
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => removeCondition(index, condIndex)}
                                                                            className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2"
                                                                            disabled={isReadOnly}
                                                                        >
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                            Delete
                                                                        </Button>
                                                                    </div>
                                                                </CardHeader>
                                                                <CardContent className="space-y-4 pt-4">
                                                                    <div className="grid grid-cols-3 gap-4">
                                                                        <div className="space-y-2">
                                                                            <Label className="text-sm font-medium">Parameter</Label>
                                                                            <Input
                                                                                value={condition.param}
                                                                                onChange={(e) => updateCondition(index, condIndex, 'param', e.target.value)}
                                                                                placeholder="e.g. status"
                                                                                className="h-9"
                                                                                disabled={isReadOnly}
                                                                            />
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            <Label className="text-sm font-medium">Operator</Label>
                                                                            <Combobox
                                                                                options={OPERATOR_OPTIONS}
                                                                                value={condition.operator}
                                                                                onValueChange={(value) => updateCondition(index, condIndex, 'operator', value)}
                                                                                placeholder="Select..."
                                                                                disabled={isReadOnly}
                                                                            />
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            <Label className="text-sm font-medium">Value</Label>
                                                                            <Input
                                                                                value={condition.value}
                                                                                onChange={(e) => updateCondition(index, condIndex, 'value', e.target.value)}
                                                                                placeholder="e.g. success"
                                                                                className="h-9"
                                                                                disabled={isReadOnly}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </CardContent>
                                                            </Card>
                                                        ))
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center py-8 text-center">
                                                            <Filter className="h-8 w-8 text-muted-foreground mb-2" />
                                                            <p className="text-xs text-muted-foreground mb-3">
                                                                No conditions set. Message will always be displayed.
                                                            </p>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                onClick={() => addCondition(index)}
                                                                disabled={isReadOnly}
                                                                className="gap-2"
                                                            >
                                                                <Plus className="h-4 w-4" />
                                                                Add Condition
                                                            </Button>
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
}
